import { defineComponent as _defineComponent } from 'vue'

const __default__ = {
  name: 'SmartValuesPanel'
}

import { ref, computed, onMounted, nextTick } from 'vue'
import { API_LOCATION } from '@/api-config'
import axios from 'axios'
import { Auth } from 'aws-amplify'
import { diacriticIncludes } from '@/util/search'

interface SmartValue {
  id: number
  variable_key: string
  display_name: string
  description: string
  example_value: string
  category: string
  source_table: string
  source_field: string
  syntax: string
}

interface CategoryGroup {
  name: string
  count: number
  values: SmartValue[]
}


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  props: {
    allowedKeys: null
  },
  emits: ["insert-value", "close"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'insert-value', value: SmartValue): void
  (e: 'close'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  /**
   * Restrict the panel to these variable keys. Omit to show the whole catalog
   * (the email builder does, and must keep doing so).
   *
   * Exists because a channel can only offer variables its renderer resolves.
   * An unresolved token ships to the customer as literal "{{booking.foo}}" —
   * tolerable to spot in an email draft, but in SMS it is plainly visible,
   * costs a segment, and cannot be recalled.
   */
  allowedKeys?: string[]
}



const searchInput = ref<HTMLInputElement | null>(null)
const loading = ref(false)
const searchQuery = ref('')
const selectedCategory = ref<string | null>(null)
const allValues = ref<SmartValue[]>([])
const copiedValueId = ref<number | null>(null)
const insertedValueId = ref<number | null>(null)



// Normalized so callers can pass either "customer.firstName" or "{{customer.firstName}}"
// without having to know which shape the catalog stores.
function normalizeKey(value: string): string {
  return (value || '').replace(/[{}]/g, '').trim()
}

const availableValues = computed((): SmartValue[] => {
  if (!props.allowedKeys || props.allowedKeys.length === 0) return allValues.value
  const allowed = new Set(props.allowedKeys.map(normalizeKey))
  return allValues.value.filter(v =>
    allowed.has(normalizeKey(v.variable_key)) || allowed.has(normalizeKey(v.syntax))
  )
})

const categoryMeta: Record<string, { icon: string, desc: string }> = {
  customer: { icon: 'tim-icons icon-single-02', desc: 'Customer name, email, phone, and address fields' },
  booking: { icon: 'tim-icons icon-calendar-60', desc: 'Booking dates, times, status, and pricing' },
  company: { icon: 'tim-icons icon-bank', desc: 'Your business name, contact info, and address' },
  'company group': { icon: 'tim-icons icon-world', desc: 'Location-specific name, contact, and address' },
  game: { icon: 'tim-icons icon-puzzle-10', desc: 'Game details, duration, difficulty, and game master info' },
  'Gift Card': { icon: 'tim-icons icon-tag', desc: 'Gift card code, value, recipient, and expiration' },
  payment: { icon: 'tim-icons icon-credit-card', desc: 'Payment method, card details, and amounts' },
  staff: { icon: 'tim-icons icon-badge', desc: 'Staff member name, email, and contact info' },
  system: { icon: 'tim-icons icon-settings-gear-63', desc: 'Current date, time, and system values' },
  transaction: { icon: 'tim-icons icon-wallet-43', desc: 'Order totals, fees, taxes, and billing info' },
  'activity log': { icon: 'tim-icons icon-paper', desc: 'Event descriptions, timestamps, and change details' },
  automation: { icon: 'tim-icons icon-refresh-02', desc: 'Automation rule name, trigger, and execution time' },
  'project management': { icon: 'tim-icons icon-components', desc: 'Ticket number, title, assignee, and project info' },
  'promo code': { icon: 'tim-icons icon-tag', desc: 'Promo code issued by a campaign and its validity dates' }
}

const filteredValues = computed(() => {
  if (!searchQuery.value) return availableValues.value
  const q = searchQuery.value.toLowerCase()
  return availableValues.value.filter(v =>
    diacriticIncludes(v.display_name, q) ||
    diacriticIncludes(v.syntax, q) ||
    (v.description && diacriticIncludes(v.description, q))
  )
})

const visibleCategories = computed((): CategoryGroup[] => {
  const map = new Map<string, SmartValue[]>()
  for (const v of availableValues.value) {
    const cat = v.category || 'other'
    if (!map.has(cat)) map.set(cat, [])
    map.get(cat)!.push(v)
  }
  return Array.from(map.entries())
    .map(([name, values]) => ({ name, count: values.length, values }))
    .sort((a, b) => a.name.localeCompare(b.name))
})

const categoryValues = computed(() => {
  if (!selectedCategory.value) return []
  return availableValues.value.filter(v => v.category === selectedCategory.value)
})

async function getAuthToken(): Promise<string> {
  const data = await Auth.currentSession()
  return data.getIdToken().getJwtToken()
}

async function loadSmartValues() {
  loading.value = true
  try {
    const token = await getAuthToken()
    const response = await axios.get(`${API_LOCATION}smart-values`, {
      headers: { Authorization: token }
    })
    if (response.data) {
      allValues.value = (response.data.values || []) as SmartValue[]
    }
  } catch (error) {
    console.error('Failed to load smart values:', error)
  } finally {
    loading.value = false
  }
}

function formatCategoryName(category: string): string {
  return category.charAt(0).toUpperCase() + category.slice(1)
}

function getCategoryIcon(category: string): string {
  return categoryMeta[category]?.icon || 'tim-icons icon-bullet-list-67'
}

function getCategoryDesc(category: string): string {
  return categoryMeta[category]?.desc || ''
}

function insertValue(value: SmartValue) {
  emit('insert-value', value)
  insertedValueId.value = value.id
  setTimeout(() => { insertedValueId.value = null }, 500)
}

async function copyValue(value: SmartValue) {
  try {
    await navigator.clipboard.writeText(value.syntax)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = value.syntax
    ta.style.cssText = 'position:fixed;opacity:0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
  }
  copiedValueId.value = value.id
  setTimeout(() => { copiedValueId.value = null }, 1500)
}

onMounted(async () => {
  await loadSmartValues()
  await nextTick()
  searchInput.value?.focus()
})

return { __sfc: true,emit, searchInput, loading, searchQuery, selectedCategory, allValues, copiedValueId, insertedValueId, props, normalizeKey, availableValues, categoryMeta, filteredValues, visibleCategories, categoryValues, getAuthToken, loadSmartValues, formatCategoryName, getCategoryIcon, getCategoryDesc, insertValue, copyValue }
}

})